import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs,
} from "@mui/material";
import React from "react";
import { useParams } from "react-router-dom";
import { COLORS } from "../../assets/theme";
import CustomLabel from "../../components/CustomLabel";
import PageHeader from "../../components/PageHeader";
import CustomTextField from "../../components/CustomTextField";
import { getSupplier } from "../../services/SuppliersService";

const DEFAULT_IMAGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <circle cx="100" cy="100" r="100" fill="#c4c4c4"/>
      <circle cx="100" cy="80" r="38" fill="#f2f2f2"/>
      <path d="M40 170c0-33 27-60 60-60s60 27 60 60" fill="#f2f2f2"/>
    </svg>`
  );

const SupplierDetails: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState(0);

  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [gender, setGender] = React.useState("");
  const [dob, setDob] = React.useState("");
  const [dobDay, setDobDay] = React.useState("");
  const [dobMonth, setDobMonth] = React.useState("");
  const [dobYear, setDobYear] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [banner, setBanner] = React.useState<string | null>(null);

  const days = React.useMemo(
    () => Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")),
    []
  );
  const months = React.useMemo(
    () => Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")),
    []
  );
  const years = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 120 }, (_, i) => String(currentYear - i));
  }, []);

  const syncDobParts = React.useCallback((dateStr: string) => {
    if (dateStr) {
      const parsed = new Date(dateStr);
      if (!Number.isNaN(parsed.getTime())) {
        setDobDay(String(parsed.getDate()).padStart(2, "0"));
        setDobMonth(String(parsed.getMonth() + 1).padStart(2, "0"));
        setDobYear(String(parsed.getFullYear()));
        return;
      }
    }
    setDobDay("");
    setDobMonth("");
    setDobYear("");
  }, []);

  React.useEffect(() => {
    const loadSupplier = async () => {
      if (!supplierId) return;
      try {
        setLoading(true);
        setError(null);
        const supplier: any = await getSupplier(supplierId);
        const dobValue =
          supplier?.DOB ||
          supplier?.dob ||
          supplier?.date_of_birth ||
          supplier?.birthdate ||
          supplier?.birth_date ||
          "";
        setFullName(supplier?.full_name || supplier?.fullName || "");
        setEmail(supplier?.user?.email || supplier?.email || "");
        setPhone(supplier?.phone || "");
        setCountry(supplier?.country || "");
        setGender(supplier?.gender || "");
        setDob(dobValue);
        syncDobParts(dobValue);
        setStatus(supplier?.active === false ? "Inactive" : "Active");
        setBanner(supplier?.banner || null);
      } catch (err) {
        console.error("Error fetching supplier details:", err);
        setError("Failed to load supplier details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadSupplier();
  }, [supplierId]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const sidePanel = (
    <Box
      flex={1}
      borderLeft={1}
      borderColor="divider"
      pl={3}
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
    >
      <Avatar
        src={typeof banner === "string" && banner.trim() ? banner : DEFAULT_IMAGE}
        variant="circular"
        alt="Supplier Banner"
        sx={{
          width: 220,
          height: 220,
          borderRadius: "50%",
          objectFit: "cover",
        }}
      />
      <Box
        justifyContent="flex-start"
        mt={5}
        alignSelf="stretch"
        width="100%"
        display="flex"
        flexDirection="column"
      >
        <CustomLabel text="Full Name" variant="subtitle1" gutterBottom={false} />
        <CustomLabel text={fullName || "-"} variant="body1" />

        <CustomLabel
          mt={2}
          text="Mobile Number"
          variant="subtitle1"
          gutterBottom={false}
        />
        <CustomLabel
          text={
            phone
              ? `${country ? `+${country} ` : ""}${phone}`
              : "-"
          }
          variant="body1"
        />

        <CustomLabel
          mt={2}
          text="Gender"
          variant="subtitle1"
          gutterBottom={false}
        />
        <CustomLabel text={gender || "-"} variant="body1" gutterBottom />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box mt={2}>
        <PageHeader
          title="Supplier Details"
          subtitle="Contact details and history for this supplier."
        />
        <Box
          sx={{
            bgcolor: COLORS.WhiteBackground,
            borderRadius: 2,
            boxShadow: 3,
            position: "relative",
            minHeight: "80vh",
          }}
        >
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && !loading && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}
{/**rr */}
          {!loading && !error && (
            <>
              <Tabs
                value={tab}
                aria-label="supplier details tabs"
                sx={{ borderBottom: 1, borderColor: "divider" }}
                onChange={handleTabChange}
              >
                <Tab
                  label="Personal Information"
                  sx={{
                    textTransform: "none",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    color: (t) => t.palette.text.secondary,
                    "&:hover": { bgcolor: "transparent" },
                  }}
                />
              </Tabs>
              <Box p={2} display="flex" gap={3}>
                <Box flex={2}>
                  {tab === 0 && (
                    <>
                      <CustomLabel
                        text="Full Name"
                        variant="subtitle1"
                        gutterBottom
                      />
                      <CustomTextField fullWidth value={fullName} disabled />

                      <CustomLabel
                        mt={2}
                        text="Email"
                        variant="subtitle1"
                        gutterBottom
                      />
                      <CustomTextField fullWidth value={email} disabled />

                      <Box mt={2} display="flex" gap={2} alignItems="flex-end">
                        <Box minWidth={0} flex={0.2}>
                          <CustomLabel
                            text="Country"
                            variant="subtitle1"
                            gutterBottom
                          />
                          <CustomTextField fullWidth value={country} disabled />
                        </Box>
                        <Box flex={0.8}>
                          <CustomLabel
                            text="Phone Number"
                            variant="subtitle1"
                            gutterBottom
                          />
                          <CustomTextField fullWidth value={phone} disabled />
                        </Box>
                      </Box>

                      <Box
                        mt={2}
                        display="grid"
                        gridTemplateColumns="1fr 1fr"
                        gap={2}
                        alignItems="flex-end"
                      >
                        <Box minWidth={0}>
                          <CustomLabel
                            text="Gender"
                            variant="subtitle1"
                            gutterBottom
                          />
                          <CustomTextField fullWidth value={gender} disabled />
                        </Box>
                        <Box
                          minWidth={0}
                          display="grid"
                          gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                          gap={1}
                        >
                          <Box minWidth={0}>
                            <CustomLabel text="Day" variant="subtitle1" gutterBottom />
                            <CustomTextField
                              fullWidth
                              minWidth="0px"
                              value={dobDay}
                              disabled
                              select
                            >
                              <MenuItem value="" disabled>
                                Day
                              </MenuItem>
                              {days.map((d) => (
                                <MenuItem key={d} value={d}>
                                  {d}
                                </MenuItem>
                              ))}
                            </CustomTextField>
                          </Box>
                          <Box minWidth={0}>
                            <CustomLabel text="Month" variant="subtitle1" gutterBottom />
                            <CustomTextField
                              fullWidth
                              minWidth="0px"
                              value={dobMonth}
                              disabled
                              select
                            >
                              <MenuItem value="" disabled>
                                Month
                              </MenuItem>
                              {months.map((m) => (
                                <MenuItem key={m} value={m}>
                                  {m}
                                </MenuItem>
                              ))}
                            </CustomTextField>
                          </Box>
                          <Box minWidth={0}>
                            <CustomLabel text="Year" variant="subtitle1" gutterBottom />
                            <CustomTextField
                              fullWidth
                              minWidth="0px"
                              value={dobYear}
                              disabled
                              select
                            >
                              <MenuItem value="" disabled>
                                Year
                              </MenuItem>
                              {years.map((y) => (
                                <MenuItem key={y} value={y}>
                                  {y}
                                </MenuItem>
                              ))}
                            </CustomTextField>
                          </Box>
                        </Box>
                      </Box>

                      <CustomLabel
                        mt={2}
                        text="Status"
                        variant="subtitle1"
                        gutterBottom
                      />
                      <CustomTextField fullWidth value={status} disabled select>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                      </CustomTextField>
                    </>
                  )}
                </Box>
                {sidePanel}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SupplierDetails;
